import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import styles from './FeedFilters.module.css'

const API_URL = process.env.REACT_APP_API_URL || ''

/* ─── Chevron ─── */
const ChevronIcon = ({ open }) => (
  <svg
    width="12" height="12" viewBox="0 0 24 24" fill="none"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
  >
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/* ─── Checkbox Section (collapsible, with search) ─── */
const CheckboxSection = ({ title, items, selected, onToggle, displayKey, valueKey }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search) return items
    return items.filter((item) => {
      const text = displayKey ? item[displayKey] : item
      return String(text || '').toLowerCase().includes(search.toLowerCase())
    })
  }, [items, search, displayKey])

  const selectedCount = selected.length

  return (
    <div className={styles.section}>
      <button className={styles.sectionHeader} onClick={() => setOpen(!open)} type="button">
        <span className={styles.sectionTitle}>
          {title}
          {selectedCount > 0 && <span className={styles.sectionBadge}>{selectedCount}</span>}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className={styles.sectionBody}>
          {items.length > 6 && (
            <input
              type="text"
              className={styles.sectionSearch}
              placeholder={`Search ${title.toLowerCase()}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          )}
          <div className={styles.checkboxList}>
            {filtered.map((item, i) => {
              const val = valueKey ? item[valueKey] : item
              const display = displayKey ? item[displayKey] : item
              const checked = selected.includes(val)
              return (
                <label key={val || i} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={checked}
                    onChange={() => onToggle(val)}
                  />
                  <span className={styles.checkboxLabel}>{display}</span>
                </label>
              )
            })}
            {filtered.length === 0 && (
              <div className={styles.emptyText}>No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* Content-specific filters each belong to one content type. Picking a value
   while the view is on "All" narrows the view to that type — a Job Type of
   "Thesis" means the user wants thesis jobs, not everything plus thesis jobs. */
const FILTER_CATEGORY = {
  job_types: 'Jobs',
  required_languages: 'Jobs',
  publishers: 'Courses',
  providers: 'News',
}

/* ─── Active chip ─── */
const Chip = ({ label, onRemove }) => (
  <span className={styles.chip}>
    {label}
    <button className={styles.chipClose} onClick={onRemove} type="button">×</button>
  </span>
)

/* ═══════════════════════════════════════════
   FeedFilters — main export
   ═══════════════════════════════════════════ */
const FeedFilters = ({ activeCategory, filters, onFiltersChange, onCategoryChange }) => {
  const [options, setOptions] = useState(null)

  // Fetch filter options once
  useEffect(() => {
    fetch(`${API_URL}/feed/filter-options`)
      .then((r) => r.json())
      .then(setOptions)
      .catch((err) => console.error('Failed to load filter options:', err))
  }, [])

  /* Toggle a value inside an array-based filter */
  const toggle = useCallback((key, val) => {
    const current = filters[key] || []
    const isAdding = !current.includes(val)
    const next = isAdding ? [...current, val] : current.filter((v) => v !== val)
    const nextFilters = { ...filters, [key]: next }

    /* Selecting a content-specific value switches the view to that content
       type, and drops the specific filters that belong to the other types —
       they would otherwise linger as chips with nothing to apply to. */
    const target = isAdding ? FILTER_CATEGORY[key] : null
    if (target && onCategoryChange && target !== activeCategory) {
      Object.entries(FILTER_CATEGORY).forEach(([k, category]) => {
        if (category !== target) nextFilters[k] = []
      })
      onCategoryChange(target)
    }

    onFiltersChange(nextFilters)
  }, [filters, onFiltersChange, onCategoryChange, activeCategory])

  /* Clear everything */
  const clearAll = useCallback(() => {
    onFiltersChange({
      domain_keys: [], country_codes: [], job_types: [],
      required_languages: [], publishers: [], providers: [],
    })
  }, [onFiltersChange])

  if (!options) return null

  /* Count active filters */
  const activeCount = Object.values(filters).reduce((sum, arr) => sum + (arr?.length || 0), 0)

  /* Which sections to show per category */
  const showDomain     = ['All', 'Jobs', 'Courses', 'Events', 'News'].includes(activeCategory)
  const showCountry    = ['All', 'Jobs', 'Courses', 'Events', 'News'].includes(activeCategory)
  const showJobType    = ['All', 'Jobs'].includes(activeCategory)
  const showLanguage   = ['All', 'Jobs'].includes(activeCategory)
  const showPublisher  = ['All', 'Courses'].includes(activeCategory)
  const showProvider   = ['All', 'News'].includes(activeCategory)

  /* Build chips for active filters */
  const chips = []
  ;(filters.domain_keys || []).forEach((k) => {
    const lbl = options.domains?.find((d) => d.key === k)?.label || k
    chips.push({ filterKey: 'domain_keys', value: k, label: `Domain: ${lbl}` })
  })
  ;(filters.country_codes || []).forEach((k) => {
    const lbl = options.countryCodes?.find((c) => c.code === k)?.name || k
    chips.push({ filterKey: 'country_codes', value: k, label: `Country: ${lbl}` })
  })
  ;(filters.job_types || []).forEach((k) => {
    const lbl = options.jobTypes?.find((j) => j.key === k)?.label || k
    chips.push({ filterKey: 'job_types', value: k, label: `Type: ${lbl}` })
  })
  ;(filters.required_languages || []).forEach((k) => {
    const lbl = options.languageCodes?.find((l) => l.code === k)?.name || k
    chips.push({ filterKey: 'required_languages', value: k, label: `Lang: ${lbl}` })
  })
  ;(filters.publishers || []).forEach((k) => {
    chips.push({ filterKey: 'publishers', value: k, label: `Publisher: ${k}` })
  })
  ;(filters.providers || []).forEach((k) => {
    chips.push({ filterKey: 'providers', value: k, label: `Provider: ${k}` })
  })

  const removeChip = (filterKey, value) => {
    const next = (filters[filterKey] || []).filter((v) => v !== value)
    onFiltersChange({ ...filters, [filterKey]: next })
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.sidebarHeader}>
        <h3 className={styles.sidebarTitle}>Filters</h3>
        {activeCount > 0 && <span className={styles.badge}>{activeCount}</span>}
      </div>

      {chips.length > 0 && (
        <div className={styles.activeFiltersArea}>
          <div className={styles.chipsWrap}>
            {chips.map((c) => (
              <Chip key={c.filterKey + c.value} label={c.label} onRemove={() => removeChip(c.filterKey, c.value)} />
            ))}
          </div>
          <button className={styles.clearBtn} onClick={clearAll} type="button">Clear all</button>
        </div>
      )}

      <div className={styles.panel}>
        {/* Common filters */}
        <div className={styles.panelGroup}>
          <div className={styles.groupLabel}>
            Common
            <span className={styles.groupHint}>applies to all</span>
          </div>
          <div className={styles.sectionsGrid}>
            {showDomain && (
              <CheckboxSection
                title="Domain"
                items={options.domains || []}
                selected={filters.domain_keys || []}
                onToggle={(val) => toggle('domain_keys', val)}
                displayKey="label"
                valueKey="key"
              />
            )}
            {showCountry && (
              <CheckboxSection
                title="Country"
                items={options.countryCodes || []}
                selected={filters.country_codes || []}
                onToggle={(val) => toggle('country_codes', val)}
                displayKey="name"
                valueKey="code"
              />
            )}
          </div>
        </div>

        {/* Category-specific filters */}
        {(showJobType || showLanguage || showPublisher || showProvider) && (
          <div className={styles.panelGroup}>
            <div className={styles.groupLabel}>
              Specific
              <span className={styles.groupHint}>
                {activeCategory !== 'All'
                  ? `for ${activeCategory}`
                  : onCategoryChange ? 'picking one switches the view' : 'per item'}
              </span>
            </div>
            <div className={styles.sectionsGrid}>
              {showJobType && (
                <CheckboxSection
                  title="Job Type"
                  items={options.jobTypes || []}
                  selected={filters.job_types || []}
                  onToggle={(val) => toggle('job_types', val)}
                  displayKey="label"
                  valueKey="key"
                />
              )}
              {showLanguage && (
                <CheckboxSection
                  title="Job Required Language"
                  items={options.languageCodes || []}
                  selected={filters.required_languages || []}
                  onToggle={(val) => toggle('required_languages', val)}
                  displayKey="name"
                  valueKey="code"
                />
              )}
              {showPublisher && (
                <CheckboxSection
                  title="Publisher"
                  items={(options.coursePublishers || []).map((p) => p.publisher)}
                  selected={filters.publishers || []}
                  onToggle={(val) => toggle('publishers', val)}
                />
              )}
              {showProvider && (
                <CheckboxSection
                  title="Provider"
                  items={(options.newsProviders || []).map((p) => p.provider)}
                  selected={filters.providers || []}
                  onToggle={(val) => toggle('providers', val)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FeedFilters
